import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { SubscriptionBenefitList } from './SubscriptionBenefitList'
import { SubscriptionUsageMeter } from './SubscriptionUsageMeter'
import {
  FREE_PLAN_BENEFITS,
  OVERAGE_LINK_LABEL,
  OVERAGE_PACK_BENEFITS,
  PRO_ANNUAL_BENEFITS,
  PRO_MONTHLY_BENEFITS,
  PRO_TRY_BENEFITS,
  SUBSCRIPTION_FOOTNOTE,
  proPriceDisplay,
  type ProBillingInterval,
} from './subscriptionPlanCopy'
import { Button } from '../Button'
import { openExternalBillingUrl } from '../../lib/authSessionPersistence'
import {
  createBillingCheckout,
  createBillingPortalSession,
  fetchBillingEntitlements,
  type BillingEntitlements,
  type BillingPlanCheckout,
} from '../../lib/billing/api'
import { supabase } from '../../lib/supabase'

const GHOST_CTA_CLASS =
  'w-full min-h-[44px] border border-interactive bg-interactive-subtle text-interactive hover:bg-interactive-subtle/80'

function PlanCard({
  children,
  highlight = false,
  compact = false,
}: {
  children: ReactNode
  highlight?: boolean
  compact?: boolean
}) {
  return (
    <div
      className={`flex flex-col rounded-lg border bg-surface-default p-5 shadow-sm ${
        highlight
          ? 'border-brand-primary ring-1 ring-brand-primary shadow-md'
          : 'border-border-default'
      } ${compact ? 'min-h-[200px]' : 'min-h-[320px]'}`}
    >
      {children}
    </div>
  )
}

function PlanBadge({
  children,
  tone = 'brand',
}: {
  children: ReactNode
  tone?: 'brand' | 'muted' | 'accent'
}) {
  const toneClass =
    tone === 'muted'
      ? 'bg-surface-muted text-text-muted'
      : tone === 'accent'
        ? 'bg-interactive-subtle text-interactive'
        : 'bg-brand-primary-subtle text-brand-primary'
  return (
    <span
      className={`inline-block self-start mb-2.5 px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide ${toneClass}`}
    >
      {children}
    </span>
  )
}

export default function ProfileSubscriptionTab() {
  const [loading, setLoading] = useState(true)
  const [entitlements, setEntitlements] = useState<BillingEntitlements | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)
  const [proInterval, setProInterval] = useState<ProBillingInterval>('monthly')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchBillingEntitlements()
      setEntitlements(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const checkout = async (plan: BillingPlanCheckout) => {
    setCheckoutLoading(plan)
    try {
      const url = await createBillingCheckout(plan)
      await openExternalBillingUrl(url, supabase)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setCheckoutLoading(null)
    }
  }

  const openPortal = async () => {
    setPortalLoading(true)
    try {
      const url = await createBillingPortalSession()
      await openExternalBillingUrl(url, supabase)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setPortalLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-text-secondary py-8">
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
        <span>Loading subscription…</span>
      </div>
    )
  }

  if (!entitlements?.billing_enabled) {
    return (
      <p className="text-body text-text-secondary py-4" data-testid="subscription-billing-disabled">
        Billing is not enabled on this environment yet.
      </p>
    )
  }

  const isPro = entitlements.tier === 'pro'
  const canManageBilling = isPro && entitlements.has_stripe_customer === true
  const atCap = entitlements.remaining <= 0
  const walletCredits = entitlements.pack_credits_remaining ?? 0
  const proDisplay = proPriceDisplay(proInterval)
  const proBenefits = proInterval === 'annual' ? PRO_ANNUAL_BENEFITS : PRO_MONTHLY_BENEFITS

  return (
    <div className="space-y-5" data-testid="profile-subscription-tab">
      {error && (
        <div className="rounded-lg border border-danger/20 bg-danger-subtle px-4 py-3 text-danger-strong text-body">
          {error}
        </div>
      )}

      {!isPro && (
        <div className="flex flex-col items-center gap-2.5 mb-1">
          <div
            className="inline-flex gap-1.5 p-1.5 rounded-xl border border-border-default bg-surface-subtle"
            role="group"
            aria-label="Billing interval for Pro"
          >
            <button
              type="button"
              data-testid="subscription-interval-monthly"
              onClick={() => setProInterval('monthly')}
              className={`min-h-[44px] px-5 rounded-lg text-body font-semibold transition-colors duration-fast ${
                proInterval === 'monthly'
                  ? 'bg-surface-default text-text-primary shadow-sm'
                  : 'text-text-muted'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              data-testid="subscription-interval-annual"
              onClick={() => setProInterval('annual')}
              className={`min-h-[44px] px-5 rounded-lg text-body font-semibold transition-colors duration-fast inline-flex items-center gap-2 ${
                proInterval === 'annual'
                  ? 'bg-surface-default text-text-primary shadow-sm'
                  : 'text-text-muted'
              }`}
            >
              Annual
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-interactive text-text-inverse text-[12px] font-bold uppercase tracking-wide">
                Save 30%
              </span>
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        <PlanCard>
          {!isPro && <PlanBadge tone="muted">Current plan</PlanBadge>}
          <h3 className="text-h3 font-semibold text-text-primary m-0 mb-1">Free</h3>
          <p className="text-[32px] font-bold tracking-tight text-text-primary m-0 mt-2">
            $0 <span className="text-body font-medium text-text-muted">/ forever</span>
          </p>
          <p className="text-small text-text-muted m-0 mb-3">No credit card</p>
          {!isPro && <SubscriptionUsageMeter entitlements={entitlements} />}
          {isPro && (
            <p className="text-small text-text-secondary mb-3">
              Included optimizations are on your Pro plan.
            </p>
          )}
          <SubscriptionBenefitList items={FREE_PLAN_BENEFITS} />
          {!isPro && atCap && (
            <button
              type="button"
              className="text-small text-interactive underline text-left mb-3 min-h-[44px]"
              data-testid="subscription-overage-link"
              onClick={() => void checkout('overage_pack')}
            >
              {OVERAGE_LINK_LABEL}
            </button>
          )}
          <Button
            type="button"
            variant="secondary"
            primaryWeight="medium"
            className="w-full min-h-[44px] mt-auto"
            disabled
            data-testid="subscription-cta-free"
          >
            {isPro ? 'Not your current plan' : 'Current plan'}
          </Button>
        </PlanCard>

        <PlanCard highlight={!isPro}>
          <PlanBadge tone={isPro ? 'muted' : 'brand'}>
            {isPro ? 'Current plan' : 'Recommended'}
          </PlanBadge>
          <h3 className="text-h3 font-semibold text-text-primary m-0 mb-1">Pro</h3>
          {isPro ? (
            <>
              <SubscriptionUsageMeter entitlements={entitlements} testId="subscription-meter-pro" />
              {entitlements.period_end && (
                <p className="text-small text-text-muted mb-3">
                  Billing period ends{' '}
                  {new Date(entitlements.period_end).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              )}
              {walletCredits > 0 && (
                <p className="text-small text-text-secondary mb-3">
                  Wallet: {walletCredits} credit{walletCredits === 1 ? '' : 's'}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="text-[32px] font-bold tracking-tight text-text-primary m-0 mt-2">
                {proDisplay.headline}{' '}
                <span className="text-body font-medium text-text-muted">
                  / {proInterval === 'annual' ? 'month' : 'month'}
                </span>
              </p>
              <p className="text-small text-text-muted m-0 mb-3">{proDisplay.subline}</p>
            </>
          )}
          <SubscriptionBenefitList items={isPro ? PRO_MONTHLY_BENEFITS : proBenefits} />
          {isPro && atCap && (
            <button
              type="button"
              className="text-small text-interactive underline text-left mb-3 min-h-[44px]"
              data-testid="subscription-overage-link-pro"
              onClick={() => void checkout('overage_pack')}
            >
              {OVERAGE_LINK_LABEL}
            </button>
          )}
          {canManageBilling ? (
            <Button
              type="button"
              variant="secondary"
              primaryWeight="medium"
              className="w-full min-h-[44px] mt-auto"
              disabled={portalLoading}
              data-testid="subscription-cta-pro-manage"
              onClick={() => void openPortal()}
            >
              {portalLoading ? 'Opening…' : 'Manage billing'}
            </Button>
          ) : isPro ? (
            <Button
              type="button"
              variant="secondary"
              primaryWeight="medium"
              className="w-full min-h-[44px] mt-auto"
              data-testid="subscription-cta-pro-setup-billing"
              onClick={() => {
                window.location.hash = 'payment'
              }}
            >
              Set up payment method
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              primaryWeight="medium"
              className="w-full min-h-[44px] mt-auto"
              disabled={checkoutLoading !== null}
              data-testid="subscription-cta-pro"
              onClick={() => void checkout(proDisplay.checkoutPlan)}
            >
              {checkoutLoading === proDisplay.checkoutPlan
                ? 'Redirecting…'
                : 'Upgrade to Pro'}
            </Button>
          )}
        </PlanCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlanCard compact>
          <PlanBadge tone="accent">Short sprint</PlanBadge>
          <h3 className="text-h3 font-semibold text-text-primary m-0 mb-1">Pro Try</h3>
          <p className="text-[32px] font-bold tracking-tight text-text-primary m-0 mt-2">
            $7 <span className="text-body font-medium text-text-muted">/ week</span>
          </p>
          <p className="text-small text-text-muted m-0 mb-3">One week · no annual commitment</p>
          <SubscriptionBenefitList items={PRO_TRY_BENEFITS} compact />
          <Button
            type="button"
            variant="ghost"
            primaryWeight="medium"
            className={GHOST_CTA_CLASS}
            disabled={checkoutLoading !== null || isPro}
            data-testid="subscription-cta-pro-try"
            onClick={() => void checkout('pro_try_weekly')}
          >
            {checkoutLoading === 'pro_try_weekly' ? 'Redirecting…' : 'Start Pro Try'}
          </Button>
        </PlanCard>

        <PlanCard compact>
          <PlanBadge tone="muted">Add-on</PlanBadge>
          <h3 className="text-h3 font-semibold text-text-primary m-0 mb-1">+5 pack</h3>
          <p className="text-[32px] font-bold tracking-tight text-text-primary m-0 mt-2">
            $5 <span className="text-body font-medium text-text-muted">/ once</span>
          </p>
          <p className="text-small text-text-muted m-0 mb-3">Wallet credits · no expiry</p>
          <SubscriptionBenefitList items={OVERAGE_PACK_BENEFITS} compact />
          <Button
            type="button"
            variant="ghost"
            primaryWeight="medium"
            className={GHOST_CTA_CLASS}
            disabled={checkoutLoading !== null}
            data-testid="subscription-cta-overage-pack"
            onClick={() => void checkout('overage_pack')}
          >
            {checkoutLoading === 'overage_pack' ? 'Redirecting…' : 'Get 5 for $5'}
          </Button>
        </PlanCard>
      </div>

      {isPro && (
        <div className="text-center pt-1">
          <button
            type="button"
            className="text-small text-text-muted underline min-h-[44px] inline-flex items-center"
            data-testid="subscription-cancel"
            disabled={portalLoading}
            onClick={() => void openPortal()}
          >
            Cancel subscription
          </button>
        </div>
      )}

      {!isPro && walletCredits > 0 && (
        <p className="text-small text-text-secondary text-center">
          Wallet: {walletCredits} credit{walletCredits === 1 ? '' : 's'}
        </p>
      )}

      <p className="text-[11px] text-text-muted text-center m-0">{SUBSCRIPTION_FOOTNOTE}</p>
    </div>
  )
}
